import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { formatDateMedium, formatDateLong } from "../utils/dateFormatter";
import { Helmet } from "react-helmet";
const API = process.env.REACT_APP_API_URL || '';

export default function MovieDetailsPage({ user, onVote, votePoints, updateVotePoints, voteWithPoints }) {
  const { movieSlug } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSlider, setShowSlider] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  // Decode the obfuscated URL ID to get real database ID
  const encodedMovieId = movie?.id || movieSlug;
  const maxPoints = user ? (user.pointsLeftToday || 0) : 10;

  useEffect(() => {
    if (!movieSlug) {
      setError("Invalid movie identifier");
      setLoading(false);
      return;
    }
    fetchMovieDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieSlug]);

  useEffect(() => {
    if (!encodedMovieId) return;
    const currentValue = Number(votePoints[encodedMovieId] || 0);
    setSliderValue(currentValue);
    if (!votePoints[encodedMovieId] && currentValue === 0) {
      updateVotePoints(encodedMovieId, "0");
    }
  }, [votePoints, encodedMovieId, updateVotePoints]);

  // Close slider when clicking outside
  useEffect(() => {
    if (!showSlider) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.simple-slider-container') && !e.target.closest('.vote-btn') && !e.target.closest('.hype-btn-detail')) {
        setShowSlider(false);
        const prevValue = Number(votePoints[encodedMovieId] || 0);
        setSliderValue(prevValue);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSlider, votePoints, encodedMovieId]);

  const fetchMovieDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API}/api/movies/${movieSlug}`);
      setMovie(res.data);
    } catch (err) {
      setError("Failed to load movie details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div className="spinner"></div>
        <p>Loading movie details...</p>
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <p style={{ color: "red", fontSize: "16px" }}>{error || "Movie not found"}</p>
        <button
          className="btn-primary"
          onClick={() => navigate("/movies")}
          style={{ marginTop: "20px" }}
        >
          Back to Movies
        </button>
      </div>
    );
  }

  const handleHypeClick = (e) => {
    e.stopPropagation();
    if (!user) {
      alert("You must be logged in to vote");
      navigate("/login");
      return;
    }
    const currentValue = Number(votePoints[encodedMovieId] || 1);
    setSliderValue(currentValue);
    updateVotePoints(encodedMovieId, currentValue.toString());
    setShowSlider(true);
  };

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setSliderValue(value);
    updateVotePoints(encodedMovieId, value.toString());
  };

  const handleVote = async () => {
    if (sliderValue === 0) return;
    setShowSlider(false);
    updateVotePoints(encodedMovieId, sliderValue.toString());
    
    // Use voteWithPoints if available, otherwise use onVote
    if (voteWithPoints) {
      const result = await voteWithPoints(encodedMovieId, sliderValue);
      if (result && result.movie && movie) {
        setMovie({ ...movie, totalPoints: result.movie.totalPoints });
      }
    } else if (onVote) {
      const result = await onVote(encodedMovieId);
      if (result && result.movie && movie) {
        setMovie({ ...movie, totalPoints: result.movie.totalPoints });
      }
    }
  };

  return (
    <div className="movie-details-page">
      <Helmet>
        <title>{movie.title} Box Office, Cast & Review | TollyHype</title>

        <meta
          name="description"
          content={`Box office, cast, reviews, trailer of ${movie.title}.`}
        />

        <link rel="canonical" href={`https://tollyhype.com/movie/${movie.slug}`} />

        <meta property="og:title" content={movie.title} />
        <meta property="og:description" content={`Box office & details of ${movie.title}`} />
        <meta property="og:image" content={movie.posterUrl} />
        <meta property="og:url" content={`https://tollyhype.com/movie/${movie.slug}`} />
      </Helmet>

      {/* Cinematic Hero / Trailer */}
      <section className="movie-hero">
        <div className="trailer-wrapper">
          {movie.youtubeId ? (
            <iframe
              className="hero-iframe"
              src={`https://www.youtube.com/embed/${movie.youtubeId}?rel=0&modestbranding=1&showinfo=0`}
              title={`${movie.title} Trailer`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="eager"
              width="100%"
              height="100%"
            />
          ) : (
            <div className="trailer-placeholder">
              <div style={{ fontSize: "80px", marginBottom: "16px" }}>🎬</div>
              <p>No trailer available</p>
            </div>
          )}
        </div>

        <div className="hero-overlay">
          <button className="back-button" onClick={() => navigate("/movies")} aria-label="Back to movies">← Back</button>

          <div className="hero-info">
            <h1 className="movie-hero-title">{movie.title}</h1>
            <div className="hero-meta">
              <span className="hero-score">⭐ {movie.totalPoints || 0} pts</span>
              <span className="hero-release">{movie.releaseDate ? formatDateMedium(movie.releaseDate) : 'TBA'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Movie Details Section (overlaps hero slightly) */}
      <div className="details-section">
        <div className="details-container">
          {/* Movie Header */}
            <div className="movie-header">
              <div className="movie-poster-side">
              {movie.posterUrl ? (
                <img 
                  src={movie.posterUrl} 
                  alt={movie.title} 
                  className="detail-poster" 
                  loading="lazy"
                  width="300"
                  height="450"
                  decoding="async"
                  fetchPriority="high"
                />
              ) : (
                <div className="detail-poster placeholder" style={{ width: '300px', height: '450px' }}>No image</div>
              )}
            </div>

            <div className="movie-header-info">
              <h1 className="movie-detail-title">{movie.title}</h1>

              <div className="rating-section">
                <div className="hype-score">
                  <span className="score-label">Community Hype</span>
                  <span className="score-value">⭐ {movie.totalPoints} Points</span>
                </div>
                <div className="release-date">
                  <span className="date-label">Release Date</span>
                  <span className="date-value">
                    {movie.releaseDate ? formatDateLong(movie.releaseDate) : 'TBA'}
                  </span>
                </div>
              </div>

              {/* Vote Section */}
              <div className="vote-section">
                <h3>Add Your Hype</h3>
                <div className="vote-controls-detail">
                  {showSlider && user ? (
                    <>
                      <div className="simple-slider-container" onClick={(e) => e.stopPropagation()}>
                        <div className="slider-labels">
                          <span className="slider-min">0</span>
                          <span className="slider-current">{sliderValue}</span>
                          <span className="slider-max">{maxPoints}</span>
                        </div>
                        <div className="slider-wrapper-inline">
                          <input
                            type="range"
                            min="0"
                            max={maxPoints}
                            value={sliderValue}
                            onChange={handleSliderChange}
                            className="points-slider-inline"
                          />
                          <div 
                            className="slider-progress-fill-inline"
                            style={{
                              width: `${maxPoints > 0 ? (sliderValue / maxPoints) * 100 : 0}%`
                            }}
                          ></div>
                        </div>
                      </div>
                      <button
                        className="vote-btn"
                        onClick={handleVote}
                        disabled={sliderValue === 0}
                        type="button"
                        aria-label="Cast your vote"
                      >
                        Cast Vote
                      </button>
                    </>
                  ) : (
                    <button
                      className="hype-btn-detail"
                      onClick={handleHypeClick}
                      disabled={!user}
                      type="button"
                      aria-label={user ? "Select points to vote" : "Login to vote"}
                    >
                      {user ? "Hype" : "Login to Vote"}
                    </button>
                  )}
                </div>
                {user && (
                  <p className="vote-info" aria-live="polite">You have {user.pointsLeftToday} points left today</p>
                )}
              </div>
            </div>

            {/* Right-side visual ad placeholder (320x250). Replace with real ad when ready. */}
            {/* <div className="aside-ad" aria-hidden="true">
              <div className="ad-placeholder">Ad placeholder — 320×250</div>
            </div> */}
          </div>

          {/* Details Grid */}
          <div className="details-grid">
            {/* Cast */}
            {movie.cast && (
              <div className="detail-card">
                <h3 className="detail-card-title">🎭 Cast</h3>
                <p className="detail-card-content">{movie.cast}</p>
              </div>
            )}

            {/* Director */}
            {movie.director && (
              <div className="detail-card">
                <h3 className="detail-card-title">🎬 Director</h3>
                <p className="detail-card-content">{movie.director}</p>
              </div>
            )}

            {/* Production Company */}
            {movie.productionCompany && (
              <div className="detail-card">
                <h3 className="detail-card-title">🏢 Production Company</h3>
                <p className="detail-card-content">{movie.productionCompany}</p>
              </div>
            )}

            {/* Box Office */}
            {movie.boxOffice && (
              <div className="detail-card">
                <h3 className="detail-card-title">💰 Box Office</h3>
                <p className="detail-card-content">{movie.boxOffice}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
